/**
 * RemoveRejectedUnitsDialog
 *
 * Confirms and runs the remove-rejected-units action for a version: removes
 * every nośnik the client rejected from the offer's draft and, in the same
 * per-unit transaction, frees any hold it still carries. Offered only on the
 * Client Decisions tab (client-offer-decision-persistence / "Manual removal
 * of rejected units from the offer draft").
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import type { BadgeProps } from '@ooh/ui/components/badge';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import type {
  FailedRemovalUnit,
  RemovedUnit,
  RemoveRejectedPreviewItem,
  RemoveRejectedPreviewResponse,
  RemoveRejectedUnitsResponse,
  UnitDecisionsListItem,
} from '@ooh/contracts';
import { useSubmitGuard } from '@ooh/ui/lib/use-submit-guard';
import { useRemoveRejectedUnits } from './api/mutations';
import { useRemoveRejectedPreview } from './api/previews';
import {
  BreakdownGroupChip,
  BreakdownPreviewState,
  distinctUnitCount,
  OutcomeResultBucket,
  OutcomeResultEntryRow,
  outcomeFailedDetail,
  type LooseT,
  type OutcomeResultUnitLookup,
} from '../../reservations/components/outcome-result-buckets';
import {
  OutcomeResultView,
  type OutcomeResultStatConfig,
} from '../../reservations/components/outcome-result-view';

// ─── Pre-commit breakdown ───────────────────────────────────────

const REMOVE_BREAKDOWN_OUTCOME_ORDER: RemoveRejectedPreviewItem['outcome'][] = [
  'removed',
  'unresolvable',
];

const REMOVE_BREAKDOWN_LABEL_KEY: Record<RemoveRejectedPreviewItem['outcome'], string> = {
  removed: 'workspace.versions.unitDecisions.remove.dialog.breakdown.removedLabel',
  unresolvable: 'workspace.versions.unitDecisions.remove.dialog.breakdown.unresolvableLabel',
};

const REMOVE_BREAKDOWN_BADGE_VARIANT: Record<
  RemoveRejectedPreviewItem['outcome'],
  BadgeProps['variant']
> = {
  removed: 'success',
  unresolvable: 'destructive',
};

interface RemoveBreakdownGroup {
  outcome: RemoveRejectedPreviewItem['outcome'];
  entries: RemoveRejectedPreviewItem[];
}

function groupRemovePreviewItems(items: RemoveRejectedPreviewResponse): RemoveBreakdownGroup[] {
  return REMOVE_BREAKDOWN_OUTCOME_ORDER.map((outcome) => ({
    outcome,
    entries: items.filter((item) => item.outcome === outcome),
  })).filter((group) => group.entries.length > 0);
}

function removeBreakdownUnresolvableDetail(t: LooseT): string {
  return t('workspace.versions.unitDecisions.remove.dialog.breakdown.unresolvableSoldReason');
}

function removeEntryWindow(entry: RemoveRejectedPreviewItem): { dateFrom?: Date; dateTo?: Date } {
  return { dateFrom: entry.dateFrom, dateTo: entry.dateTo };
}

function RemoveBreakdownView({
  preview,
  lookup,
}: {
  preview: {
    isLoading: boolean;
    isError: boolean;
    data: RemoveRejectedPreviewResponse | undefined;
    refetch: () => void;
  };
  lookup: OutcomeResultUnitLookup;
}) {
  const { t } = useTranslation('offers');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('workspace.versions.unitDecisions.remove.result.unknownUnit');

  const items = preview.data ?? [];
  const groups = groupRemovePreviewItems(items);

  return (
    <BreakdownPreviewState
      preview={preview}
      testIdPrefix="remove-breakdown"
      loadingLabel={t('workspace.versions.unitDecisions.remove.dialog.breakdown.loading')}
      errorLabel={t('workspace.versions.unitDecisions.remove.dialog.breakdown.error')}
      retryLabel={t('workspace.versions.unitDecisions.remove.dialog.breakdown.retryBtn')}
      isEmpty={items.length === 0}
      emptyLabel={t('workspace.versions.unitDecisions.remove.dialog.breakdown.empty')}
    >
      <div className="space-y-2" data-testid="remove-breakdown">
        <div className="flex flex-wrap gap-2" data-testid="remove-breakdown-groups">
          {groups.map((group) => (
            <BreakdownGroupChip
              key={group.outcome}
              testId={`remove-breakdown-group-${group.outcome}`}
              variant={REMOVE_BREAKDOWN_BADGE_VARIANT[group.outcome]}
              label={looseT(REMOVE_BREAKDOWN_LABEL_KEY[group.outcome])}
              count={distinctUnitCount(group.entries)}
            />
          ))}
        </div>

        {groups.map((group) => (
          <OutcomeResultBucket<RemoveRejectedPreviewItem>
            key={group.outcome}
            trigger={
              <span className="text-sm font-medium">
                {looseT(REMOVE_BREAKDOWN_LABEL_KEY[group.outcome])}
              </span>
            }
            entries={group.entries}
            emptyLabel=""
            testIdPrefix="remove-breakdown-entry"
            renderEntry={(entry) => (
              <OutcomeResultEntryRow
                unit={lookup.get(entry.mediaUnitId)}
                {...removeEntryWindow(entry)}
                detail={
                  entry.outcome === 'unresolvable'
                    ? removeBreakdownUnresolvableDetail(looseT)
                    : undefined
                }
                unknownUnitLabel={unknownUnitLabel}
              />
            )}
          />
        ))}
      </div>
    </BreakdownPreviewState>
  );
}

// ─── Result view ──────────────────────────────────────────────

function buildUnitLookup(items: UnitDecisionsListItem[]): OutcomeResultUnitLookup {
  const map: OutcomeResultUnitLookup = new Map();
  for (const item of items) {
    map.set(item.mediaUnitId, item);
  }
  return map;
}

function RemoveResultView({
  result,
  lookup,
}: {
  result: RemoveRejectedUnitsResponse;
  lookup: OutcomeResultUnitLookup;
}) {
  const { t } = useTranslation('offers');
  const unknownUnitLabel = t('workspace.versions.unitDecisions.remove.result.unknownUnit');
  const stats: OutcomeResultStatConfig[] = [
    {
      icon: CheckCircle2,
      tone: 'success',
      label: t('workspace.versions.unitDecisions.remove.result.removedLabel'),
      value: result.removed.length,
      testId: 'remove-result-removed-count',
    },
    {
      icon: XCircle,
      tone: 'destructive',
      label: t('workspace.versions.unitDecisions.remove.result.failedLabel'),
      value: result.failed.length,
      testId: 'remove-result-failed-count',
    },
  ];

  return (
    <OutcomeResultView className="grid-cols-2" stats={stats}>
      <OutcomeResultBucket<RemovedUnit>
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.unitDecisions.remove.result.removedLabel')}
          </span>
        }
        entries={result.removed}
        emptyLabel={t('workspace.versions.unitDecisions.remove.result.removedEmpty')}
        testIdPrefix="remove-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
      <OutcomeResultBucket<FailedRemovalUnit>
        trigger={
          <span className="text-sm font-medium">
            {t('workspace.versions.unitDecisions.remove.result.failedLabel')}
          </span>
        }
        entries={result.failed}
        emptyLabel={t('workspace.versions.unitDecisions.remove.result.failedEmpty')}
        testIdPrefix="remove-outcome-entry"
        renderEntry={(entry) => (
          <OutcomeResultEntryRow
            unit={lookup.get(entry.mediaUnitId)}
            detail={outcomeFailedDetail(entry, t as LooseT)}
            unknownUnitLabel={unknownUnitLabel}
          />
        )}
      />
    </OutcomeResultView>
  );
}

// ─── Dialog (trigger → confirm → result) ──

export function RemoveRejectedUnitsDialog({
  offerId,
  versionId,
  items,
  onClose,
}: {
  offerId: string;
  versionId: number;
  /** The version's currently-fetched unit decisions, used to resolve each
   * result entry's display identity (GRW id, address, city). Snapshotted at
   * commit time (`resultLookup` below), mirroring the retired
   * ReleaseRejectedUnitsDialog's own rationale — removed units leave the
   * draft as a side effect of this very action, so they would have already
   * disappeared from the parent's refetched list by the time the result
   * view renders. */
  items: UnitDecisionsListItem[];
  onClose: () => void;
}) {
  const { t } = useTranslation('offers');
  const removeMutation = useRemoveRejectedUnits();
  const submitGuard = useSubmitGuard();
  const preview = useRemoveRejectedPreview(offerId, versionId);
  const lookup = React.useMemo(() => buildUnitLookup(items), [items]);
  const [result, setResult] = React.useState<RemoveRejectedUnitsResponse | null>(null);
  const [resultLookup, setResultLookup] = React.useState<OutcomeResultUnitLookup>(new Map());
  const canConfirm = !removeMutation.isPending && !preview.isLoading && !preview.isError;

  async function handleConfirm() {
    if (!canConfirm || !submitGuard.tryStart()) return;
    const lookupAtCommit = buildUnitLookup(items);
    try {
      const response = await removeMutation.mutateAsync({ offerId, versionId });
      setResult(response);
      setResultLookup(lookupAtCommit);
    } catch {
      // Handled by useRemoveRejectedUnits's onError (translated toast) — dialog stays open.
    } finally {
      submitGuard.finish();
    }
  }

  return (
    <ModalDialog
      onClose={onClose}
      testId="remove-rejected-units-dialog"
      header={
        <h2 className="text-base font-semibold">
          {t('workspace.versions.unitDecisions.remove.dialog.title')}
        </h2>
      }
      footer={
        result == null ? (
          <>
            <Button variant="outline" onClick={onClose}>
              {t('workspace.versions.unitDecisions.remove.dialog.cancelBtn')}
            </Button>
            <Button
              disabled={!canConfirm}
              onClick={() => void handleConfirm()}
              data-testid="remove-rejected-units-confirm-button"
            >
              {t('workspace.versions.unitDecisions.remove.dialog.confirmBtn')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>
            {t('workspace.versions.unitDecisions.remove.result.closeBtn')}
          </Button>
        )
      }
    >
      {result == null ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('workspace.versions.unitDecisions.remove.dialog.description')}
          </p>
          <RemoveBreakdownView preview={preview} lookup={lookup} />
        </div>
      ) : (
        <RemoveResultView result={result} lookup={resultLookup} />
      )}
    </ModalDialog>
  );
}
